import { Cable, House, MessageCircle } from 'lucide-react';
import type { NavId } from '../hooks/use-sidebar-nav';

// The rail destinations' glyphs. Most are named after the lucide set they come from
// (Figma `16/house`, `16/cable`), so each is the named lucide glyph at the DS's 16px
// rail size rather than a redrawn path. Two are inlined vectors instead, each because
// lucide has nothing that is the same mark: Your Agents' DS glyph is a bespoke two-ring
// mark (Figma `16/target`, node 11378:120038) where lucide's `Target` has three, and
// WhatsApp is a brand glyph, which the set carries none of.
// Shared by the wide rail and the mobile drawer so one destination reads the same
// on both.

export function ChatIcon() {
  return <MessageCircle size={16} aria-hidden />;
}

export function HomeIcon() {
  return <House size={16} aria-hidden />;
}

export function YourAgentsIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8.0005 14.668C11.6826 14.668 14.6675 11.6831 14.6675 8.00098C14.6675 4.3189 11.6826 1.33398 8.0005 1.33398C4.31841 1.33398 1.3335 4.3189 1.3335 8.00098C1.3335 11.6831 4.31841 14.668 8.0005 14.668Z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.00049 10.6904C9.4861 10.6904 10.6904 9.4861 10.6904 8.00049C10.6904 6.51487 9.4861 5.31055 8.00049 5.31055C6.51487 5.31055 5.31055 6.51487 5.31055 8.00049C5.31055 9.4861 6.51487 10.6904 8.00049 10.6904Z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// No longer a rail destination (Connectors moved into Settings), but still the mark for the
// connectors row in Your Agents' narrow destination list — hence the caller-chosen size.
export function ConnectorsIcon({ size = 16 }: { size?: number } = {}) {
  return <Cable size={size} aria-hidden />;
}

// The DS's own sidebar WhatsApp mark, exported from Figma (`Sidebar-Menu`) and inlined unchanged apart
// from taking its colour from the rail: lucide carries no brand glyphs, so nothing in the set IS this
// icon and the nearest neighbours all name something else. The ring is one path read `evenodd`, which is
// what makes it hollow — filling it turns the bubble into a solid blob at this size.
//
// The export sits in a 24 box but only spans 5–19 of it, where lucide draws on a 24 grid with 2px of
// padding — so at the same box size this mark comes out visibly smaller than the destinations above it.
// The viewBox crops to 16.8 units centred on the glyph, which is the size at which its 14 units cover
// the same share of the box as lucide's 20. The vector is untouched; only the frame around it moves.
export function ConnectWhatsappIcon() {
  return (
    <svg width={16} height={16} viewBox="3.6 3.6 16.8 16.8" fill="none" aria-hidden>
      <path
        d="M9.87988 8.76953C9.98982 8.76953 10.1296 8.73015 10.2695 9.05957C10.4095 9.39956 10.7598 10.24 10.7998 10.3301C10.8398 10.4201 10.8696 10.5199 10.8096 10.6299C10.7496 10.7498 10.7296 10.8199 10.6396 10.9199C10.5497 11.0199 10.4598 11.1398 10.3799 11.2197C10.2899 11.3097 10.1998 11.4003 10.2998 11.5703C10.3999 11.7405 10.75 12.3099 11.2598 12.7598C11.9198 13.3398 12.4804 13.5204 12.6504 13.6104C12.8199 13.7 12.9197 13.6799 13.0195 13.5703C13.1295 13.4603 13.4602 13.0705 13.5703 12.9004C13.6903 12.7205 13.8001 12.7496 13.96 12.8096C14.1199 12.8695 14.9691 13.2799 15.1396 13.3701H15.1201C15.29 13.4601 15.4102 13.5003 15.4502 13.5703C15.49 13.641 15.4894 13.9805 15.3496 14.3799C15.2097 14.7897 14.5206 15.15 14.1904 15.2002C13.9005 15.2402 13.5202 15.2598 13.1104 15.1299C12.8604 15.0499 12.5399 14.9495 12.1299 14.7695C10.4002 14.0295 9.28056 12.2899 9.19043 12.1797C9.11043 12.0697 8.49031 11.2501 8.49023 10.4102C8.49023 9.57069 8.92959 9.15068 9.08984 8.98047C9.24975 8.81057 9.43977 8.76958 9.5498 8.76953H9.87988Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 5C15.8642 5 19 8.13579 19 12C19 15.8642 15.8642 19 12 19C10.8873 19 9.82991 18.7369 8.89453 18.2734L5.92383 18.9795C5.65324 19.0438 5.36873 18.9534 5.18555 18.7441C5.00244 18.5348 4.95022 18.2411 5.0498 17.9814L5.97461 15.5693C5.35541 14.5215 5 13.3011 5 12C5 8.13579 8.13579 5 12 5ZM12 6.5C8.96421 6.5 6.5 8.96421 6.5 12C6.5 13.1363 6.84278 14.1917 7.43262 15.0723C7.56839 15.2749 7.59722 15.5311 7.50977 15.7588L6.95898 17.1914L8.81641 16.75C8.99761 16.7069 9.1884 16.7334 9.35156 16.8232C10.1332 17.2536 11.0392 17.5 12 17.5C15.0358 17.5 17.5 15.0358 17.5 12C17.5 8.96421 15.0358 6.5 12 6.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export const NAV_ICONS: Record<NavId, React.ReactNode> = {
  chats: <ChatIcon />,
  feed: <HomeIcon />,
  agents: <YourAgentsIcon />,
  'connect-whatsapp': <ConnectWhatsappIcon />,
};
